import { PermissionService, UserDto, validInt } from 'booksprout'
import { AuthenticatedUser } from '../modules/_base/auth/auth.service.interface'
import authStoreGetters from '../modules/authentication/store/getters'

/**
 * Who the visitor is, and what they are allowed to do.
 *
 * The members of `CommonAuthUserMixin`, which existed to break a circular import rather than for its
 * own sake — `BsUpgrade` in the component bundle wanted `authenticatedUser` and `hasTieredPermission`
 * and nothing else. The cycle is why ~300 specs hand-write a barrel mock, so the rule that kept it
 * broken still applies here: **this file may import only the authentication store and the isomorphic
 * `booksprout` package** — never the package barrel or the component bundle.
 *
 * Everything is a getter on the returned object, not a value, because the store is a `reactive()` that
 * changes under it: a component reading `user.authenticatedUser` re-renders on login, and one that
 * destructured a value would not. That is the same reason the mixin used class getters (which
 * vue-class-component compiles to computeds).
 */

/**
 * Module scope on purpose: it never needs to be reactive, and re-instantiating a UserDto and a
 * PermissionService per component would be waste.
 */
const DummyUser = {
  ...new UserDto(),
  permissionQuery: new PermissionService().configure(new UserDto())
}

export function useUserHelpers () {
  const hasTieredPermission = (needsRole: string): boolean => {
    return !!helpers.mimicOrAuthenticatedUser.permissionQuery.hasTieredPermission(needsRole)
  }

  const helpers = {
    /**
     * Falls back to a blank user with a configured permission query, so a template reading
     * `authenticatedUser.name` before the session loads gets '' rather than throwing.
     */
    get authenticatedUser (): AuthenticatedUser {
      const user = authStoreGetters.authenticatedUser as AuthenticatedUser
      return user || DummyUser
    },

    get mimicUser (): AuthenticatedUser {
      const user = authStoreGetters.mimicUser as AuthenticatedUser
      return user || DummyUser
    },

    get isMimic (): boolean {
      return validInt(helpers.mimicUser.id)
    },

    /**
     * The user whose permissions and data the screen should reflect: an admin mimicking someone sees
     * what that person sees.
     */
    get mimicOrAuthenticatedUser (): AuthenticatedUser {
      return helpers.isMimic ? helpers.mimicUser : helpers.authenticatedUser
    },

    hasTieredPermission
  }

  return helpers
}
